/**
 * @file PaginationComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Pagination
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-02-27
 * @modified 2025-07-31
 * @author DLTCode
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */
"use client";

import { PaginationComponentProps } from "@/types/ui";
import { ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

const PaginationComponent = <T,>({
  table,
  pageIndex,
  pageCount,
}: PaginationComponentProps<T>) => {
  const t = useTranslations("TableComponent.Pagination");

  const currentPage = pageIndex + 1;
  const totalPages = pageCount;

  const [pageInput, setPageInput] = useState<string>(String(currentPage));
  const parsedPageInput = Number(pageInput);
  const isPageInputInvalid =
    Number.isNaN(parsedPageInput) ||
    parsedPageInput < 1 ||
    parsedPageInput > totalPages;
  const normalizedInputPage = Math.min(
    Math.max(parsedPageInput, 1),
    totalPages
  );
  const isSamePageAsCurrent =
    !isPageInputInvalid && normalizedInputPage === currentPage;
  const isSinglePage = totalPages <= 1;
  const isGoToPageDisabled =
    totalPages <= 0 || isPageInputInvalid || isSamePageAsCurrent;
  const inputWidth = Math.max(39, 20 + pageInput.length * 10);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const goToPage = () => {
    const parsedPage = Number(pageInput);

    if (Number.isNaN(parsedPage)) return;

    const safePage = Math.min(Math.max(parsedPage, 1), totalPages);
    table.setPageIndex(safePage - 1);
    setPageInput(String(safePage));
  };

  return (
    <section>
      <div className="table__pagination">
        {/* Go to page */}
        <div className="table__pagination-jump">
          <span className="table__pagination-label">{t("gotoPage")}</span>

          <input
            id="pagination-input"
            type="number"
            inputMode="numeric"
            maxLength={4}
            disabled={isSinglePage}
            tabIndex={isSinglePage ? -1 : 0}
            value={pageInput}
            onChange={(e) =>
              setPageInput(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") goToPage();
            }}
            className="table__pagination-input"
            style={{ width: `${inputWidth}px` }}
            aria-label={t("gotoPage")}
          />

          <button
            type="button"
            className="table__pagination-iconButton table__pagination-iconButton--filled"
            onClick={goToPage}
            disabled={isGoToPageDisabled || isSinglePage}
            tabIndex={isSinglePage ? -1 : 0}
            aria-label={t("gotoPage")}
            title={t("gotoPage")}
          >
            <ChevronsRight size={14} strokeWidth={2.2} />
          </button>
        </div>

        {/* Previous Page Button */}
        <button
          type="button"
          onClick={() => table.previousPage()}
          disabled={currentPage <= 1}
          className="table__pagination-navButton"
          aria-label={t("previousPage")}
          title={t("previousPage")}
        >
          <ChevronLeft size={14} />
          <span>{t("previousPage")}</span>
        </button>

        {/* Page indicator */}
        <div className="table__pagination-status" aria-live="polite">
          <span className="table__pagination-statusCurrent">{currentPage}</span>
          <span className="table__pagination-statusSeparator">/</span>
          <span className="table__pagination-statusTotal">{totalPages}</span>
        </div>

        {/* Next Page Button */}
        <button
          type="button"
          onClick={() => table.nextPage()}
          disabled={currentPage >= totalPages}
          className="table__pagination-navButton"
          aria-label={t("nextPage")}
          title={t("nextPage")}
        >
          <span>{t("nextPage")}</span>
          <ChevronRight size={14} />
        </button>
      </div>
    </section>
  );
};

export default PaginationComponent;
